'use client';

// First-login auto-launch for the "Tour of Jigyasa".
//
// Mounted INSIDE <AshraryaProvider> so it can drive the tour engine directly.
// While has_seen_intro === false the welcome tour auto-plays on EVERY login.
// When the tour ends (finished or skipped) we ask the user whether to keep
// auto-playing it; only an explicit "No, stop the auto tour" flips the flag.
// The owner can always re-run it from the help bubble ("give me a tour").

import { useEffect, useRef, useState } from "react";
import { useTourEngine, sendAshraryaMessage } from "../packages/aumonline-ashraya/src";
import { api } from "./api";

const INTRO_TOUR_IDS = ["jigyasa-tour", "jigyasa-tour-solo", "jigyasa-tour-staff"];

export function AshrayaIntroLauncher({ user, config, onSeenIntro, isSolo = false }) {
  const { startTour } = useTourEngine();
  const firedRef = useRef(false);
  const [askRepeat, setAskRepeat] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (firedRef.current) return;
    if (!user || user.has_seen_intro) return;

    // Staff get their own tour that only points at menus they actually have.
    const variant = user.role === "staff" ? "staff" : isSolo ? "solo" : "full";

    const timer = setTimeout(async () => {
      // Guard INSIDE the timer so StrictMode's mount→unmount→remount in dev
      // (which clears the first timer) doesn't permanently block the launch.
      if (firedRef.current) return;
      firedRef.current = true; // only ever fire once per login session
      try {
        const res = await sendAshraryaMessage({
          webhookUrl: config.webhookUrl,
          headers: config.requestHeaders,
          payload: {
            app_namespace: config.appNamespace,
            intent: "intro_tour",
            variant,
            user: config.user,
            session_id: null,
            channel: "text",
            input: { type: "text", content: "Tour", language: "en", intent: "intro_tour" },
            client_meta: {
              variant,
              current_path: typeof window !== "undefined" ? window.location.pathname : "/",
            },
          },
        });

        const tour = res?.tour;
        if (tour && Array.isArray(tour.steps) && tour.steps.length > 0) {
          startTour(tour);
        }
      } catch (err) {
        if (config.debug) {
          // eslint-disable-next-line no-console
          console.warn("[Ashraya] intro tour launch failed:", err);
        }
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [user, config, startTour, isSolo]);

  // When the auto-launched welcome tour ends, ask whether to keep auto-playing.
  useEffect(() => {
    if (!user || user.has_seen_intro) return undefined;
    const onTour = (e) => {
      const d = e.detail || {};
      if ((d.type === "tour_completed" || d.type === "tour_abandoned") && INTRO_TOUR_IDS.includes(d.tour_id)) {
        if (firedRef.current) setAskRepeat(true);
      }
    };
    window.addEventListener("ashraya:tour", onTour);
    return () => window.removeEventListener("ashraya:tour", onTour);
  }, [user]);

  const stopAutoTour = async () => {
    setSaving(true);
    try {
      await api.post("/auth/me/seen-intro");
      onSeenIntro?.();
    } catch {
      /* non-fatal: flag will flip on a later attempt */
    }
    setSaving(false);
    setAskRepeat(false);
  };

  if (!askRepeat) return null;

  return (
    <div className="fixed left-1/2 bottom-6 z-[75] w-[calc(100%-2rem)] max-w-md -translate-x-1/2" data-testid="intro-repeat-card">
      <div className="rounded-2xl border-2 border-rose-500 bg-white px-4 py-3 shadow-2xl">
        <div className="text-sm font-semibold text-zinc-900">Want the welcome tour again next time you log in?</div>
        <div className="mt-1 text-xs text-zinc-500">
          You can always re-run it from the pink bubble — just type "give me a tour".
        </div>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            data-testid="intro-repeat-yes"
            onClick={() => setAskRepeat(false)}
            className="flex-1 rounded-full border border-zinc-300 px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
          >
            Yes, show it again
          </button>
          <button
            type="button"
            data-testid="intro-repeat-stop"
            onClick={stopAutoTour}
            disabled={saving}
            className="flex-1 rounded-full bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60"
          >
            No, stop the auto tour
          </button>
        </div>
      </div>
    </div>
  );
}
